import { recordingModeOf, resolveSummaryLayout, type TSummaryBlockType } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Alert, Button, Collapse, Drawer, Menu, TextInput, UnstyledButton } from "@mantine/core"
import { useMediaQuery } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import {
  IconDotsVertical,
  IconEye,
  IconEyeOff,
  IconFileDescription,
  IconPencil,
  IconRefresh,
  IconShare,
  IconTrash,
  IconUserPlus,
} from "@tabler/icons-react"
import { useNavigate } from "@tanstack/react-router"
import { cloneElement, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { formatClientLabel } from "../../../lib/client-info"
import { formatDateTime } from "../../../lib/format-date"
import { canReprocess } from "../../../lib/meeting-reprocess"
import { displayMeetingTitle } from "../../../lib/meeting-title"
import { resolveSpeakerColor } from "../../../lib/speaker-colors"
import {
  coerceSpeakerQuotes,
  normalizeAnalysis,
  normalizeSegmentSpeakers,
  speakerLabelDisplayMap,
} from "../../../lib/speaker-mapping"
import {
  deriveMeetingSpeakers,
  speakerDisplayLabel,
  speakerDisplayNameList,
  speakerEntityId,
  speakerVMFromName,
} from "../../../lib/speaker-vm"
import { ExportMenu } from "../../ExportMenu"
import { MeetingLanguageSwitcher } from "../../MeetingLanguageSwitcher"
import { MeetingMaterials } from "../../MeetingMaterials"
import { MeetingReportBlock } from "../../MeetingReportBlock"
import { MeetingSourceLanguage } from "../../MeetingSourceLanguage"
import { MeetingTabs } from "../../MeetingTabs"
import { Player } from "../../Player"
import { MiniPlayer } from "../../Player/MiniPlayer"
import { RecordingModeChip } from "../../RecordingModeChip"
import { ReprocessForm } from "../../ReprocessForm"
import { SpeakerStatusBadges } from "../../SpeakerChip/SpeakerStatusBadges"
import { SpeakerEditModal } from "../../SpeakerEditModal"
import { SpeakerNamingStrip } from "../../SpeakerNamingStrip"
import { StaggerReveal } from "../../StaggerReveal"
import { WhoSpokePanel } from "../../WhoSpokePanel"
import { ActionItemsCard } from "../cards/ActionItemsCard"
import { AttendeesCard } from "../cards/AttendeesCard"
import { CoachQuestionsCard } from "../cards/CoachQuestionsCard"
import { CostCard } from "../cards/CostCard"
import { DecisionsCard } from "../cards/DecisionsCard"
import { FollowUpCard } from "../cards/FollowUpCard"
import { OpenQuestionsCard } from "../cards/OpenQuestionsCard"
import { QualityCard } from "../cards/QualityCard"
import { QuotesCard } from "../cards/QuotesCard"
import { SentimentCard } from "../cards/SentimentCard"
import { SourceCard } from "../cards/SourceCard"
import { SuggestedJargonCard } from "../cards/SuggestedJargonCard"
import { SummaryCard } from "../cards/SummaryCard"
import { TalkTimeCard } from "../cards/TalkTimeCard"
import { TopicsCard } from "../cards/TopicsCard"
import { MeetingPlayerProvider, useMeetingPlayerController } from "../MeetingPlayerContext"
import { StatusPill } from "../parts/StatusPill"
import classes from "../styles.module.css"
import { displayChapters, displayCoachSuggestions } from "./helpers"
import type { IProps } from "./interfaces"

/**
 * MeetingSummaryView — the dumb assembled page for the Lexicanon
 * post-recording summary. Receives a fully-loaded meeting + analysis from
 * the route-level smart wrapper (`/meetings/$id`) and renders the title
 * chrome, then the summary. On a wide screen the player sits in the sticky
 * side rail. On a narrow screen the page uses Summary / Details tabs and a
 * docked player. Two modals (speaker editor + reprocess form) stay here.
 *
 * Local state is presentation-only:
 *   - `editingSpeaker` — currently-open SpeakerCard modal
 *   - `reprocessOpen`  — currently-open ReprocessForm modal
 *
 * Side effects are limited to navigator.clipboard writes for the Share /
 * Copy CTAs and a notification toast for the Jira "coming soon" stub.
 */
export function MeetingSummaryView({
  meeting,
  analysis,
  companyId,
  speakers,
  speakerVMs,
  speakerColors,
  actions,
  onSpeakerEdited,
  onRenameSubject,
  initialSeekSeconds,
  initialTab,
  editContext,
  languageContext,
  reveal,
  jargonSuggestions = [],
  onAcceptJargon,
  onDismissJargon,
  jargonBusyId,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const navigate = useNavigate()
  // 62em is the Mantine `md` breakpoint. Below it the page uses tabs + a dock.
  const isNarrow = useMediaQuery("(max-width: 61.99em)", false, { getInitialValueInEffect: false })
  // Shared transport for the one recording player → every fragment chip
  // (decisions, actions, questions, quotes) plays/pauses + rewinds against it.
  const {
    controls: playerControls,
    playerRef,
    onPlayingChange,
    onProgress,
    onSpeedChange,
  } = useMeetingPlayerController(!!meeting.recording_url)
  // Unified speaker VM: use what the host derived, else derive from `meeting` so
  // the panel and modal always have a roster to resolve against.
  const resolvedSpeakerVMs = useMemo(() => speakerVMs ?? deriveMeetingSpeakers(meeting), [speakerVMs, meeting])
  // Prefer the full roster (recognised + diarised) so meetings where no speaker
  // was voiceprint-matched still render their Speaker 1… speakers everywhere
  // (legend, colours, panel). Falls back to the recognised list.
  const displayNames = useMemo(
    () => speakerDisplayNameList(resolvedSpeakerVMs.speakers, i18n),
    [resolvedSpeakerVMs.speakers, i18n],
  )
  const speakerNames = useMemo(
    () => (speakers && speakers.length > 0 ? speakers : displayNames),
    [speakers, displayNames],
  )
  // One map for the meta line, the naming strip, talk-share, and the player.
  const colorMap = speakerColors ?? resolvedSpeakerVMs.colorMap
  const labelMap = useMemo(() => speakerLabelDisplayMap(resolvedSpeakerVMs.speakers), [resolvedSpeakerVMs])
  const unnamedSpeakers = useMemo(
    () =>
      resolvedSpeakerVMs.speakers
        .filter((vm) => vm.needsAttention)
        .slice()
        .sort((a, b) => (b.talkPercent ?? 0) - (a.talkPercent ?? 0) || a.slot - b.slot),
    [resolvedSpeakerVMs.speakers],
  )
  const namingPeople = useMemo(
    () =>
      resolvedSpeakerVMs.speakers
        .filter((vm) => !vm.needsAttention)
        .map((vm) => {
          const name = speakerDisplayLabel(vm, i18n)
          return {
            key: speakerEntityId(vm) ?? vm.label ?? name,
            name,
            color: resolveSpeakerColor(name, colorMap),
            initials: vm.initials,
          }
        }),
    [resolvedSpeakerVMs.speakers, i18n, colorMap],
  )
  const unnamedCount = unnamedSpeakers.length
  const firstUnnamed = unnamedSpeakers[0]
  // Streaming segments persist with raw diarization labels ("Speaker_S1" etc.).
  // Normalise the speaker field against the entity label→name map so downstream
  // matchers compare resolved names against resolved names.
  const segments = useMemo(
    () => normalizeSegmentSpeakers(meeting.streaming_segments, labelMap.size > 0 ? labelMap : speakerNames),
    [meeting.streaming_segments, labelMap, speakerNames],
  )
  const durationMinutes = meeting.duration_minutes ?? 0
  // Count DISTINCT speakers from the roster (post channel-merge it holds one
  // entry per person) so a far-end echo that slipped through auto-merge and was
  // merged by rename doesn't inflate the headcount or the per-person cost. Falls
  // back to the LLM's contributor list only when the roster is empty.
  const distinctSpeakerCount = new Set(speakerNames).size
  const attendeeCount = distinctSpeakerCount > 0 ? distinctSpeakerCount : (analysis.attendee_contributions?.length ?? 0)

  // Anchored meeting cost = attendees × duration × blended hourly rate. Replaces
  // the LLM's `quality.estimated_cost_eur` guess, which was unanchored and wildly
  // off. Rate is a per-org setting (default €75/h) resolved server-side.
  const costHourlyRateEur = meeting.cost_hourly_rate_eur ?? 75
  const estimatedCostEur = useMemo(
    () =>
      durationMinutes > 0 && attendeeCount > 0
        ? Math.round((durationMinutes / 60) * attendeeCount * costHourlyRateEur)
        : 0,
    [durationMinutes, attendeeCount, costHourlyRateEur],
  )

  const coachSuggestions = useMemo(() => displayCoachSuggestions(analysis, meeting), [analysis, meeting])
  const chapters = useMemo(() => displayChapters(analysis, meeting), [analysis, meeting])

  const sourceLanguageCode = (meeting.analysis_locale ?? languageContext?.sourceLocale ?? "en").toLowerCase()
  const sourceLanguageOptions = useMemo(
    () => [
      { code: "en", label: i18n._(msg`English`) },
      { code: "nl", label: i18n._(msg`Dutch`) },
      { code: "de", label: i18n._(msg`German`) },
      { code: "fr", label: i18n._(msg`French`) },
      { code: "es", label: i18n._(msg`Spanish`) },
      { code: "it", label: i18n._(msg`Italian`) },
      { code: "pt", label: i18n._(msg`Portuguese`) },
    ],
    [i18n],
  )
  const sourceLanguageLabel =
    sourceLanguageOptions.find((o) => o.code === sourceLanguageCode)?.label ?? sourceLanguageCode.toUpperCase()

  // Title shown in the header. Meetings started without a subject
  // carry an empty `subject`; once post-processing produces topics, render the
  // top topic as the title (the "leave it blank, let the LLM name it" flow).
  // Inline rename still edits the raw `subject`, so it stays empty until the
  // user types one — see beginTitleEdit/titleDraft below.
  const displayTitle = displayMeetingTitle(meeting.subject, meeting.topics, i18n)

  // Speaker management modal state — keyed by entity id (falling back to the
  // raw label for legacy meetings without entity rows).
  const [editingSpeaker, setEditingSpeaker] = useState<string | null>(null)

  // Reprocess modal state — kebab "Reprocess" opens the existing
  // ReprocessForm (template picker + transcription backend + custom
  // prompt). Gated on the meeting having finished its initial pipeline —
  // re-running while still live would race the live finalize.
  const [reprocessOpen, setReprocessOpen] = useState(false)
  const reprocessable = canReprocess(meeting.status)

  // Inline title rename — restores the click-to-edit subject behaviour from the
  // legacy detail view. Interactive only when the host supplies onRenameSubject
  // (tenant present + write access); otherwise the title is plain static text.
  const canRenameTitle = !!onRenameSubject
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState(meeting.subject ?? "")
  const [savingTitle, setSavingTitle] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const [detailTab, setDetailTab] = useState<"summary" | "details">(initialTab === "details" ? "details" : "summary")
  const [seenInitialTab, setSeenInitialTab] = useState(initialTab)
  if (initialTab !== seenInitialTab) {
    setSeenInitialTab(initialTab)
    if (initialTab === "details" || initialTab === "summary") setDetailTab(initialTab)
  }
  const [sheetOpen, setSheetOpen] = useState(false)
  const [reportHot, setReportHot] = useState(false)
  const [reportTick, setReportTick] = useState(0)
  const [focusSignal, setFocusSignal] = useState(0)
  const panelRootRef = useRef<HTMLDivElement>(null)
  const reportRef = useRef<HTMLDivElement>(null)
  const reportTimer = useRef<number | null>(null)
  const prevUnnamed = useRef(0)

  const openSpeakers = useCallback((focusUnnamed: boolean): void => {
    setPanelOpen(true)
    if (focusUnnamed) setFocusSignal((current) => current + 1)
  }, [])

  useEffect(() => {
    if (prevUnnamed.current > 0 && unnamedCount === 0) setPanelOpen(false)
    prevUnnamed.current = unnamedCount
  }, [unnamedCount])

  useEffect(() => {
    return () => {
      if (reportTimer.current !== null) window.clearTimeout(reportTimer.current)
    }
  }, [])

  const beginTitleEdit = (): void => {
    setTitleDraft(meeting.subject ?? "")
    setEditingTitle(true)
  }

  const cancelTitleEdit = (): void => {
    setTitleDraft(meeting.subject ?? "")
    setEditingTitle(false)
  }

  const commitTitle = async (): Promise<void> => {
    const next = titleDraft.trim()
    if (!next || next === meeting.subject) {
      cancelTitleEdit()
      return
    }
    setSavingTitle(true)
    try {
      await onRenameSubject?.(next)
      setEditingTitle(false)
    } catch {
      cancelTitleEdit()
    } finally {
      setSavingTitle(false)
    }
  }

  // Per-speaker quotes / profile / auto-title, keyed by entity id (falling
  // back to the raw label for legacy meetings). Name keys stay as a last
  // resort so a meeting served without entity rows still opens the modal.
  const speakerQuotes = useMemo(() => {
    const byName = new Map<string, string[]>()
    for (const sq of coerceSpeakerQuotes(analysis.speaker_quotes)) {
      byName.set(sq.speaker, sq.quotes)
    }
    const map = new Map<string, string[]>()
    for (const vm of resolvedSpeakerVMs.speakers) {
      const key = speakerEntityId(vm) ?? vm.label ?? vm.displayName
      const quotes = byName.get(vm.displayName) ?? (vm.label ? byName.get(vm.label) : undefined)
      if (quotes) map.set(key, quotes)
    }
    for (const [name, quotes] of byName) {
      if (!map.has(name)) map.set(name, quotes)
    }
    return map
  }, [analysis.speaker_quotes, resolvedSpeakerVMs.speakers])

  // Auto-detected role title from analysis.attendee_contributions, normalised
  // against the entity label→name map (the raw analysis can carry
  // "Name (Speaker_N)" suffixes that the SpeakerCard wouldn't expect).
  const speakerAutoTitles = useMemo(() => {
    const map = new Map<string, string>()
    if (!analysis.attendee_contributions) return map
    const normalised = normalizeAnalysis(
      { attendee_contributions: analysis.attendee_contributions },
      labelMap.size > 0 ? labelMap : speakerLabelDisplayMap(resolvedSpeakerVMs.speakers),
    )
    const contributions = normalised.attendee_contributions
    if (!Array.isArray(contributions)) return map
    const byName = new Map<string, string>()
    for (const ac of contributions) {
      if (ac !== null && typeof ac === "object" && "name" in ac && "role" in ac) {
        const name = ac.name
        const role = ac.role
        if (typeof name === "string" && typeof role === "string" && role.length > 0) byName.set(name, role)
      }
    }
    for (const vm of resolvedSpeakerVMs.speakers) {
      const key = speakerEntityId(vm) ?? vm.label ?? vm.displayName
      const role = byName.get(vm.displayName)
      if (role) map.set(key, role)
    }
    for (const [name, role] of byName) {
      if (!map.has(name)) map.set(name, role)
    }
    return map
  }, [analysis.attendee_contributions, labelMap, resolvedSpeakerVMs.speakers])

  const speakerProfileMap = useMemo(() => {
    const byName = new Map((meeting.speaker_profiles ?? []).map((p) => [p.name, p]))
    const map = new Map(byName)
    for (const vm of resolvedSpeakerVMs.speakers) {
      const key = speakerEntityId(vm) ?? vm.label ?? vm.displayName
      const profile = byName.get(vm.displayName)
      if (profile) map.set(key, profile)
    }
    return map
  }, [meeting.speaker_profiles, resolvedSpeakerVMs.speakers])

  const speakerSegmentsMap = useMemo(() => {
    const map = new Map<string, NonNullable<typeof segments>>()
    if (!segments) return map
    for (const seg of segments) {
      const list = map.get(seg.speaker) ?? []
      list.push(seg)
      map.set(seg.speaker, list)
    }
    return map
  }, [segments])

  const editingVM = editingSpeaker
    ? (resolvedSpeakerVMs.speakers.find((vm) => speakerEntityId(vm) === editingSpeaker) ??
      resolvedSpeakerVMs.byLabel.get(editingSpeaker) ??
      resolvedSpeakerVMs.byName.get(editingSpeaker) ??
      speakerVMFromName(editingSpeaker, colorMap))
    : null
  const editingKey = editingVM ? (speakerEntityId(editingVM) ?? editingVM.label ?? editingVM.displayName) : null

  const handleCopySummary = async (): Promise<void> => {
    if (!analysis.summary) return
    try {
      await navigator.clipboard.writeText(analysis.summary)
      notifications.show({ message: i18n._(msg`Summary copied to clipboard`), color: "success" })
    } catch {
      notifications.show({ message: i18n._(msg`Could not copy — clipboard not available`), color: "danger" })
    }
  }

  const handleShare = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      notifications.show({ message: i18n._(msg`Link copied to clipboard`), color: "success" })
    } catch {
      notifications.show({ message: i18n._(msg`Could not copy link`), color: "danger" })
    }
  }

  const handleJiraSync = (): void => {
    notifications.show({
      message: i18n._(msg`Jira sync coming soon — your action items will export to Jira issues here.`),
      color: "gray",
    })
  }

  // ---- Modular block layout ----
  // The backend resolves the per-company config against the registry and serves
  // it as meeting.summary_layout; we fall back to the registry defaults (today's
  // layout) when it's absent. Each visible block renders via renderBlock and is
  // keyed; nulls (absent optional data) are dropped so StaggerReveal's delay
  // cells line up exactly as before.
  const summaryLayout = meeting.summary_layout ?? resolveSummaryLayout()

  const showReport = useCallback((): void => {
    const report = summaryLayout.find((block) => block.type === "visual_report")
    setDetailTab(report?.column === "side" ? "details" : "summary")
    setReportHot(true)
    setReportTick((tick) => tick + 1)
    if (reportTimer.current !== null) window.clearTimeout(reportTimer.current)
    reportTimer.current = window.setTimeout(() => {
      setReportHot(false)
      reportTimer.current = null
    }, 1800)
  }, [summaryLayout])

  useEffect(() => {
    if (reportTick === 0) return
    reportRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
  }, [reportTick])

  const renderBlock = (type: TSummaryBlockType): React.JSX.Element | null => {
    switch (type) {
      case "player":
        return meeting.recording_url ? (
          <Player
            ref={playerRef}
            src={meeting.recording_url}
            segments={segments}
            speakerColors={colorMap}
            speakerNames={speakerNames}
            speakers={resolvedSpeakerVMs.speakers}
            chapters={chapters}
            showChaptersWhenEmpty
            showLegend={false}
            onPlayingChange={onPlayingChange}
            onProgress={onProgress}
            onSpeedChange={onSpeedChange}
            initialSeekSeconds={initialSeekSeconds}
          />
        ) : null
      case "summary":
        return (
          <SummaryCard
            text={analysis.summary}
            onCopy={() => handleCopySummary().catch(() => undefined)}
            editContext={editContext}
          />
        )
      case "decisions":
        return (
          <DecisionsCard
            decisions={analysis.decisions ?? []}
            segments={segments}
            seekOffsets={analysis.seek_offsets?.decisions}
            editContext={editContext}
          />
        )
      case "action_items":
        return (
          <ActionItemsCard
            items={analysis.action_points ?? []}
            speakerColorMap={colorMap}
            speakerNames={speakerNames}
            onExportItem={handleJiraSync}
            segments={segments}
            seekOffsets={analysis.seek_offsets?.action_points}
            editContext={editContext}
          />
        )
      case "open_questions":
        return (
          <OpenQuestionsCard
            questions={analysis.open_questions ?? []}
            segments={segments}
            seekOffsets={analysis.seek_offsets?.open_questions}
            editContext={editContext}
          />
        )
      case "topics":
        return <TopicsCard topics={analysis.topics ?? []} />
      case "attendees":
        return (
          <AttendeesCard
            attendees={analysis.attendee_contributions ?? []}
            speakerNames={speakerNames}
            talkShare={meeting.talk_share}
            speakerColorMap={colorMap}
            actionPoints={analysis.action_points ?? []}
            editContext={editContext}
          />
        )
      case "quotes":
        return (
          <QuotesCard
            quotes={coerceSpeakerQuotes(analysis.speaker_quotes)}
            speakerNames={speakerNames}
            speakerColorMap={colorMap}
            segments={segments}
            resolveSpeaker={(name) => resolvedSpeakerVMs.byName.get(name) ?? resolvedSpeakerVMs.byLabel.get(name)}
            editContext={editContext}
          />
        )
      case "sentiment":
        return <SentimentCard sentiment={analysis.sentiment} editContext={editContext} />
      case "coach_questions":
        return <CoachQuestionsCard suggestions={coachSuggestions} />
      case "follow_up":
        return analysis.follow_up ? <FollowUpCard followUp={analysis.follow_up} /> : null
      case "quality":
        return <QualityCard quality={analysis.quality} editContext={editContext} />
      case "talk_time":
        return (
          <TalkTimeCard
            speakerNames={speakerNames}
            talkShare={meeting.talk_share}
            durationMinutes={durationMinutes}
            speakerColorMap={colorMap}
          />
        )
      case "cost":
        return (
          <CostCard
            costEur={estimatedCostEur}
            hourlyRate={costHourlyRateEur}
            attendeeCount={attendeeCount}
            speakerNames={speakerNames}
            talkShare={meeting.talk_share}
            speakerColorMap={colorMap}
          />
        )
      case "source":
        return <SourceCard meeting={meeting} />
      case "suggested_jargon":
        return onAcceptJargon && onDismissJargon ? (
          <SuggestedJargonCard
            suggestions={jargonSuggestions}
            onAccept={onAcceptJargon}
            onDismiss={onDismissJargon}
            busyId={jargonBusyId}
          />
        ) : null
      case "visual_report":
        // Smart wrapper: fetches report-fit + generated reports and self-hides
        // (renders null) when there's nothing to offer. Needs a tenant to scope
        // its calls, so it's skipped when companyId is absent (e.g. standalone).
        return companyId ? (
          <div
            ref={reportRef}
            className={classes.reportAnchor}
            data-highlight={reportHot ? "true" : undefined}
            data-testid="meeting-report"
          >
            <MeetingReportBlock meetingId={meeting.id} companyId={companyId} />
          </div>
        ) : null
      default:
        return null
    }
  }

  const blocksIn = (column: "main" | "side"): React.JSX.Element[] =>
    summaryLayout
      .filter((b) => b.column === column && b.visible && b.type !== "player")
      .sort((a, b) => a.order - b.order)
      .map((b) => {
        const el = renderBlock(b.type)
        return el ? cloneElement(el, { key: b.type }) : null
      })
      .filter((el): el is React.JSX.Element => el !== null)

  const playerNode = renderBlock("player")
  const mainNodes = blocksIn("main")
  const sideNodes = blocksIn("side")
  const recordingMode = recordingModeOf(meeting.template_id)
  const allNamed = unnamedCount === 0 && namingPeople.length > 0
  const showDock = Boolean(isNarrow && meeting.recording_url)
  const metaParts: string[] = []
  if (meeting.start_time) metaParts.push(formatDateTime(i18n, meeting.start_time))
  if (durationMinutes > 0) metaParts.push(i18n._(msg`${durationMinutes} min`))
  if (meeting.source) metaParts.push(formatClientLabel(meeting.client_info, meeting.source))

  const mainColumn = (
    <div className={classes.main}>
      <StaggerReveal active={!!reveal}>{mainNodes}</StaggerReveal>
      {companyId ? <MeetingMaterials meetingId={meeting.id} companyId={companyId} processed /> : null}
    </div>
  )
  const sideColumn = (
    <aside className={classes.side} data-testid="meeting-summary-side">
      {isNarrow ? null : playerNode}
      <StaggerReveal active={!!reveal} baseMs={120}>
        {sideNodes}
      </StaggerReveal>
    </aside>
  )

  return (
    <MeetingPlayerProvider value={playerControls}>
      <div className={classes.shell} data-testid="meeting-summary" data-dock={showDock ? "true" : undefined}>
        <div className={classes.head}>
          <div>
            {editingTitle ? (
              <TextInput
                autoFocus
                classNames={{ input: classes.titleInput }}
                value={titleDraft}
                disabled={savingTitle}
                aria-label={i18n._(msg`Meeting title`)}
                onChange={(e) => setTitleDraft(e.currentTarget.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitTitle()
                  if (e.key === "Escape") cancelTitleEdit()
                }}
              />
            ) : canRenameTitle ? (
              <h1 className={classes.title}>
                <UnstyledButton
                  className={classes.titleButton}
                  title={i18n._(msg`Click to rename`)}
                  onClick={beginTitleEdit}
                >
                  {displayTitle}
                  <IconPencil size={16} className={classes.titleEditIcon} aria-hidden />
                </UnstyledButton>
              </h1>
            ) : (
              <h1 className={classes.title}>{displayTitle}</h1>
            )}
            <div className={classes.metaRow}>
              {metaParts.length > 0 ? <span>{metaParts.join(" · ")}</span> : null}
              {meeting.status !== "summarized" ? <StatusPill phase={meeting.status} /> : null}
              {recordingMode === "lite" ? <RecordingModeChip mode={recordingMode} /> : null}
              {allNamed ? (
                // The strip renders a list. A button cannot contain a ul.
                // biome-ignore lint/a11y/useSemanticElements: list cannot be a child of button
                <div
                  role="button"
                  tabIndex={0}
                  className={classes.peopleButton}
                  data-testid="speaker-people-button"
                  aria-label={i18n._(msg`Who spoke`)}
                  aria-expanded={panelOpen}
                  onClick={() => setPanelOpen((open) => !open)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault()
                      setPanelOpen((open) => !open)
                    }
                  }}
                >
                  <SpeakerNamingStrip unnamedCount={0} people={namingPeople} onNameClick={() => undefined} />
                </div>
              ) : null}
            </div>
          </div>
          <div className={classes.actions}>
            {languageContext?.onChangeSourceLanguage && (
              <MeetingSourceLanguage
                languageCode={sourceLanguageCode}
                displayName={sourceLanguageLabel}
                options={sourceLanguageOptions}
                isPending={languageContext.isChangingSourceLanguage}
                onChangeLanguage={languageContext.onChangeSourceLanguage}
              />
            )}
            {languageContext && (
              <MeetingLanguageSwitcher
                sourceLocale={languageContext.sourceLocale}
                availableTranslations={languageContext.availableTranslations}
                currentLocale={languageContext.currentLocale}
                isTranslating={languageContext.isTranslating}
                onLocaleChange={languageContext.onLocaleChange}
              />
            )}
            <Button variant="default" size="sm" leftSection={<IconShare size={14} />} onClick={handleShare}>
              <Trans>Share</Trans>
            </Button>
            {companyId && <ExportMenu meetingId={meeting.id} companyId={companyId} />}
            {companyId ? (
              <Button variant="default" size="sm" onClick={showReport}>
                <Trans>Make a report</Trans>
              </Button>
            ) : null}
            {actions && (
              <Menu shadow="md" width={220} position="bottom-end">
                <Menu.Target>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    aria-label={i18n._(msg`More actions`)}
                    data-testid="meeting-summary-more"
                  >
                    <IconDotsVertical size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconFileDescription size={14} />}
                    onClick={() => navigate({ to: "/meetings/$id/transcript", params: { id: meeting.id } })}
                  >
                    <Trans>View transcript</Trans>
                  </Menu.Item>
                  {reprocessable && (
                    <Menu.Item leftSection={<IconRefresh size={14} />} onClick={() => setReprocessOpen(true)}>
                      <Trans>Reprocess</Trans>
                    </Menu.Item>
                  )}
                  {actions.onConvertToLite && (
                    <Menu.Item onClick={actions.onConvertToLite}>
                      <Trans>Convert to Lite…</Trans>
                    </Menu.Item>
                  )}
                  {actions.onManageAccess && (
                    <Menu.Item leftSection={<IconUserPlus size={14} />} onClick={actions.onManageAccess}>
                      <Trans>Manage access</Trans>
                    </Menu.Item>
                  )}
                  {actions.onToggleHidden && (
                    <Menu.Item
                      leftSection={actions.isHidden ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                      onClick={actions.onToggleHidden}
                    >
                      {actions.isHidden ? <Trans>Unhide</Trans> : <Trans>Hide</Trans>}
                    </Menu.Item>
                  )}
                  {actions.onDelete && (
                    <>
                      <Menu.Divider />
                      <Menu.Item leftSection={<IconTrash size={14} />} color="danger" onClick={actions.onDelete}>
                        <Trans>Delete permanently</Trans>
                      </Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
          </div>
        </div>

        {languageContext?.hasTranslationError && (
          <Alert color="danger" variant="light">
            <Trans>Translation failed. Showing the original analysis.</Trans>
          </Alert>
        )}
        {languageContext?.isTranslating && languageContext.currentLocale !== languageContext.sourceLocale && (
          <Alert color="info" variant="light">
            <Trans>
              Translating to {languageContext.currentLocale.toUpperCase()} — showing the source analysis until it's
              ready (typically under a minute).
            </Trans>
          </Alert>
        )}

        {unnamedCount > 0 ? (
          <SpeakerNamingStrip
            unnamedCount={unnamedCount}
            firstUnnamedLabel={firstUnnamed ? speakerDisplayLabel(firstUnnamed, i18n) : undefined}
            firstUnnamedShare={firstUnnamed?.talkPercent != null ? firstUnnamed.talkPercent / 100 : undefined}
            people={namingPeople}
            onNameClick={() => openSpeakers(true)}
          />
        ) : null}

        <Collapse in={panelOpen}>
          <div ref={panelRootRef}>
            {panelOpen && resolvedSpeakerVMs.speakers.length > 0 ? (
              <WhoSpokePanel
                meetingId={meeting.id}
                speakers={resolvedSpeakerVMs.speakers}
                focusFirstUnnamedSignal={focusSignal}
                onEditSpeaker={(speaker) =>
                  setEditingSpeaker(speakerEntityId(speaker) ?? speaker.label ?? speaker.displayName)
                }
              />
            ) : null}
          </div>
        </Collapse>

        {isNarrow ? (
          <>
            <MeetingTabs
              meetingId={meeting.id}
              current={detailTab}
              onChange={(next) => {
                setDetailTab(next)
                if (next === "summary" && initialTab === "details") {
                  void navigate({
                    to: "/meetings/$id",
                    params: { id: meeting.id },
                    search: (prev) => ({ t: prev.t }),
                    replace: true,
                  })
                }
              }}
            />
            {detailTab === "details" ? sideColumn : mainColumn}
          </>
        ) : (
          <div className={classes.grid}>
            {mainColumn}
            {sideColumn}
          </div>
        )}

        {showDock ? (
          <>
            <Drawer
              opened={sheetOpen}
              onClose={() => setSheetOpen(false)}
              position="bottom"
              keepMounted
              size="auto"
              title={<Trans>Recording</Trans>}
              classNames={{ body: classes.playerSheet }}
            >
              {playerNode}
            </Drawer>
            <MiniPlayer onExpand={() => setSheetOpen(true)} />
          </>
        ) : null}

        <SpeakerEditModal
          meetingId={meeting.id}
          opened={editingSpeaker !== null}
          speaker={editingVM}
          allSpeakers={resolvedSpeakerVMs.speakers}
          quotes={editingKey ? (speakerQuotes.get(editingKey) ?? []) : []}
          profile={editingKey ? speakerProfileMap.get(editingKey) : undefined}
          autoTitle={editingKey ? speakerAutoTitles.get(editingKey) : undefined}
          speakerSegments={
            // Segments are keyed by RAW diarization label, but name-keyed surfaces
            // (quotes/attendees/legend) open this modal with a display string —
            // which misses for a still-unnamed speaker (observed 2026-07-22: audio
            // samples present via the label-keyed panel, gone via a name-keyed
            // open). Resolve the VM first, then key by ITS label, falling back to
            // the clicked string for legacy name-labelled data.
            (editingVM?.label ? speakerSegmentsMap.get(editingVM.label) : undefined) ??
            (editingSpeaker ? speakerSegmentsMap.get(editingSpeaker) : undefined)
          }
          audioUrl={meeting.recording_url}
          titleBadges={
            editingVM ? (
              <SpeakerStatusBadges status={editingVM.status} recognitionSource={editingVM.recognitionSource} />
            ) : null
          }
          onClose={() => setEditingSpeaker(null)}
          onEdited={onSpeakerEdited}
        />

        <ReprocessForm
          meetingId={meeting.id}
          templateId={meeting.template_id}
          meetingLanguage={meeting.analysis_locale}
          hasRecording={!!meeting.recording_url}
          opened={reprocessOpen}
          onClose={() => setReprocessOpen(false)}
          onReprocessed={() => {
            setReprocessOpen(false)
            onSpeakerEdited?.()
          }}
        />
      </div>
    </MeetingPlayerProvider>
  )
}
